import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { UserPlus, Save, X, Eye, EyeOff, Camera, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const CreateUser = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const isEditing = !!userId;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    role: 'common_user',
    phone: '',
    bio: '',
    address: '',
    companyId: '',
    departmentId: '',
    positionId: '',
    timezone: 'America/Sao_Paulo'
  });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [avatarBase64, setAvatarBase64] = useState(null);

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Carregar funções disponíveis e dados organizacionais
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🚀 Carregando dados...');
        const token = localStorage.getItem('token');
        console.log('🔑 Token encontrado:', token ? 'Sim' : 'Não');
        
        // Carregar roles
        const rolesResponse = await api.get('/users/roles');
        console.log('✅ Roles carregadas:', rolesResponse.data);
        setAvailableRoles(rolesResponse.data.roles);
        
        // Carregar empresas
        const companiesResponse = await api.get('/organizational/companies');
        console.log('✅ Empresas carregadas:', companiesResponse.data);
        setCompanies(companiesResponse.data.companies || []);
        
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
        console.error('❌ Detalhes do erro:', error.response?.data);
        console.error('❌ Status do erro:', error.response?.status);
        toast.error('Erro ao carregar dados necessários');
      }
    };

    if (user?.role === 'admin') {
      console.log('👤 Usuário é admin, carregando dados...');
      loadData();
    } else {
      console.log('❌ Usuário não é admin:', user?.role);
    }
  }, [user]);

  // Carregar dados do usuário para edição
  useEffect(() => {
    if (isEditing && userId) {
      const loadUser = async () => {
        try {
          setIsLoading(true);
          const response = await api.get(`/users/${userId}`);
          const userData = response.data.user;
          
          setFormData({
            username: userData.username || '',
            email: userData.email || '',
            fullName: userData.fullName || '',
            role: userData.role || 'common_user',
            phone: userData.phone || '',
            bio: userData.bio || '',
            address: userData.address || '',
            companyId: userData.companyId || '',
            departmentId: userData.departmentId || '',
            positionId: userData.positionId || '',
            timezone: userData.timezone || 'America/Sao_Paulo'
          });
          
          if (userData.avatar_base64) {
            setAvatarBase64(userData.avatar_base64);
          }
        } catch (error) {
          console.error('Erro ao carregar usuário:', error);
          toast.error('Erro ao carregar dados do usuário');
          navigate('/dashboard');
        } finally {
          setIsLoading(false);
        }
      };

      loadUser();
    }
  }, [isEditing, userId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Se a empresa foi alterada, limpar departamento e cargo
    if (name === 'companyId') {
      setFormData(prev => ({
        ...prev,
        departmentId: '',
        positionId: ''
      }));
      // Carregar departamentos da empresa selecionada
      if (value) {
        loadDepartments(value);
      } else {
        setDepartments([]);
        setPositions([]);
      }
    }
    
    // Se o departamento foi alterado, limpar cargo
    if (name === 'departmentId') {
      setFormData(prev => ({
        ...prev,
        positionId: ''
      }));
      // Carregar cargos do departamento selecionado
      if (value) {
        loadPositions(formData.companyId, value);
      } else {
        setPositions([]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('Apenas arquivos de imagem são permitidos');
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
        setAvatarBase64(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setAvatarBase64(null);
  };

  const loadDepartments = async (companyId) => {
    try {
              const response = await api.get(`/organizational/companies/${companyId}/departments`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      setDepartments([]);
    }
  };

  const loadPositions = async (companyId, departmentId = null) => {
    try {
      let response;
      if (departmentId) {
        // Carregar cargos do departamento específico
        response = await api.get(`/organizational/companies/${companyId}/positions`);
        const allPositions = response.data.positions || [];
        // Filtrar apenas cargos do departamento selecionado
        const filteredPositions = allPositions.filter(pos => pos.department_id === parseInt(departmentId));
        setPositions(filteredPositions);
      } else {
        // Carregar todos os cargos da empresa
        response = await api.get(`/organizational/companies/${companyId}/positions`);
        setPositions(response.data.positions || []);
      }
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
      setPositions([]);
    }
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      toast.error('Nome de usuário é obrigatório');
      return false;
    }

    if (formData.username.trim() && !/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      toast.error('Nome de usuário deve conter apenas letras, números e underscore');
      return false;
    }

    if (!formData.email.trim()) {
      toast.error('Email é obrigatório');
      return false;
    }

    if (!formData.fullName.trim()) {
      toast.error('Nome completo é obrigatório');
      return false;
    }

    if (!formData.companyId) {
      toast.error('Empresa é obrigatória');
      return false;
    }

    if (!isEditing && !password) {
      toast.error('Senha é obrigatória para novos usuários');
      return false;
    }

    if (password && password.length < 8) {
      toast.error('Senha deve ter pelo menos 8 caracteres');
      return false;
    }

    if (password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(password)) {
      toast.error('Senha deve conter pelo menos uma letra minúscula, uma maiúscula, um número e um caractere especial');
      return false;
    }

    if (password !== confirmPassword) {
      toast.error('Senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const userData = {
        ...formData,
        password: password || undefined,
        avatar_base64: avatarBase64,
        company_id: parseInt(formData.companyId),
        department_id: formData.departmentId ? parseInt(formData.departmentId) : null,
        position_id: formData.positionId ? parseInt(formData.positionId) : null,
        timezone: formData.timezone
      };

      let response;
      if (isEditing) {
        response = await api.put(`/users/${userId}`, userData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        response = await api.post('/users', userData);
        toast.success('Usuário criado com sucesso!');
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao salvar usuário';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <UserPlus className="h-6 w-6 text-primary-600" />
                <h1 className="text-2xl font-semibold text-gray-900">
                  {isEditing ? 'Editar Usuário' : 'Criar Novo Usuário'}
                </h1>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                  {avatarBase64 ? (
                    <img 
                      src={avatarBase64} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-semibold text-gray-600">
                        {formData.fullName ? formData.fullName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  )}
                </div>
                
                <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                  <Camera className="h-4 w-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Foto do Perfil</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Clique na câmera para selecionar uma imagem. Tamanho máximo: 5MB
                </p>
                {avatarBase64 && (
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="inline-flex items-center space-x-2 text-red-600 hover:text-red-700 text-sm"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Remover foto</span>
                  </button>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome de Usuário *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  {availableRoles.map(role => (
                    <option key={role.name} value={role.name}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="America/Sao_Paulo">Brasil (São Paulo) - GMT-3</option>
                  <option value="America/New_York">Estados Unidos (Nova York) - GMT-5</option>
                  <option value="America/Los_Angeles">Estados Unidos (Los Angeles) - GMT-8</option>
                  <option value="America/Chicago">Estados Unidos (Chicago) - GMT-6</option>
                  <option value="America/Denver">Estados Unidos (Denver) - GMT-7</option>
                  <option value="America/Toronto">Canadá (Toronto) - GMT-5</option>
                  <option value="America/Vancouver">Canadá (Vancouver) - GMT-8</option>
                  <option value="Europe/London">Reino Unido (Londres) - GMT+0</option>
                  <option value="Europe/Paris">França (Paris) - GMT+1</option>
                  <option value="Europe/Berlin">Alemanha (Berlim) - GMT+1</option>
                  <option value="Europe/Madrid">Espanha (Madrid) - GMT+1</option>
                  <option value="Europe/Rome">Itália (Roma) - GMT+1</option>
                  <option value="Europe/Amsterdam">Países Baixos (Amsterdã) - GMT+1</option>
                  <option value="Europe/Stockholm">Suécia (Estocolmo) - GMT+1</option>
                  <option value="Europe/Oslo">Noruega (Oslo) - GMT+1</option>
                  <option value="Europe/Copenhagen">Dinamarca (Copenhague) - GMT+1</option>
                  <option value="Europe/Helsinki">Finlândia (Helsinque) - GMT+2</option>
                  <option value="Europe/Moscow">Rússia (Moscou) - GMT+3</option>
                  <option value="Asia/Tokyo">Japão (Tóquio) - GMT+9</option>
                  <option value="Asia/Shanghai">China (Xangai) - GMT+8</option>
                  <option value="Asia/Seoul">Coreia do Sul (Seul) - GMT+9</option>
                  <option value="Asia/Singapore">Singapura - GMT+8</option>
                  <option value="Asia/Bangkok">Tailândia (Bangkok) - GMT+7</option>
                  <option value="Asia/Jakarta">Indonésia (Jacarta) - GMT+7</option>
                  <option value="Asia/Kolkata">Índia (Calcutá) - GMT+5:30</option>
                  <option value="Asia/Dubai">Emirados Árabes Unidos (Dubai) - GMT+4</option>
                  <option value="Australia/Sydney">Austrália (Sydney) - GMT+10</option>
                  <option value="Australia/Melbourne">Austrália (Melbourne) - GMT+10</option>
                  <option value="Australia/Perth">Austrália (Perth) - GMT+8</option>
                  <option value="Pacific/Auckland">Nova Zelândia (Auckland) - GMT+12</option>
                  <option value="Africa/Cairo">Egito (Cairo) - GMT+2</option>
                  <option value="Africa/Johannesburg">África do Sul (Joanesburgo) - GMT+2</option>
                  <option value="Africa/Lagos">Nigéria (Lagos) - GMT+1</option>
                  <option value="UTC">UTC (Tempo Universal Coordenado) - GMT+0</option>
                </select>
              </div>
            </div>

            {/* Organizational Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa *
                </label>
                <select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione uma empresa</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departamento
                </label>
                <select
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                  disabled={!formData.companyId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Selecione um departamento (opcional)</option>
                  {departments.map(department => (
                    <option key={department.id} value={department.id}>{department.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cargo
                </label>
                <select
                  name="positionId"
                  value={formData.positionId}
                  onChange={handleInputChange}
                  disabled={!formData.companyId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">Selecione um cargo (opcional)</option>
                  {positions.map(position => (
                    <option key={position.id} value={position.id}>{position.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biografia
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Conte um pouco sobre você..."
              />
            </div>

            {/* Password Section */}
            {!isEditing && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Senha</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Mínimo 8 caracteres, com letra maiúscula, minúscula, número e caractere especial
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>{isLoading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Usuário')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateUser;
